import { useEffect, useMemo, useRef, useState } from "react";
import {
    Calendar,
    ChevronDown,
    Filter as FilterIcon,
    Layers,
    Tag,
    X,
    type LucideIcon,
} from "lucide-react";
import { useIsFetching } from "@tanstack/react-query";
import { useDashboardStore } from "../store/dashboardStore";
import { useFilterOptions } from "../api/queries";
import {
    daysBetween,
    fmtBR,
    toISO,
} from "../lib/format";
import { getPeriodLabel } from "../lib/filters";
import { RangeCalendar } from "./RangeCalendar";
import { SearchableList } from "./SearchableList";
import { DashSkeleton } from "./Skeleton";
import type { Filters, PeriodPreset } from "../types/reports";
import { cn } from "@/lib/utils";

interface FilterDef {
    key: keyof Filters;
    icon: LucideIcon;
    label: string;
    defaultV: string;
    kind: "period" | "list";
    options: { v: string; l: string }[];
}

const PERIOD_OPTIONS = [
    { v: "today", l: "Hoje" },
    { v: "yesterday", l: "Ontem" },
    { v: "7d", l: "7 dias" },
    { v: "30d", l: "30 dias" },
    { v: "90d", l: "90 dias" },
];

export function FilterBar() {
    const filters = useDashboardStore((s) => s.filters);
    const setFilter = useDashboardStore((s) => s.setFilter);
    const { data: opts, isLoading: optsLoading } = useFilterOptions();
    const projects = opts?.projects ?? [];
    const categories = (opts?.categories ?? []).map((c) => ({
        id: c.id ?? c.value ?? c.name ?? "",
        name: c.name ?? c.label ?? c.id ?? "",
    }));
    // Projeto/Categoria dependem do fetch — período é estático, não bloqueia.
    const listsLoading = optsLoading && !opts;

    const FILTER_DEFS: FilterDef[] = useMemo(
        () => [
            {
                key: "period",
                icon: Calendar,
                label: "Período",
                defaultV: "yesterday",
                kind: "period",
                options: PERIOD_OPTIONS,
            },
            {
                key: "project",
                icon: Layers,
                label: "Projeto",
                defaultV: "all",
                kind: "list",
                options: [
                    { v: "all", l: "Todos os projetos" },
                    ...projects.map((p) => ({ v: p.id, l: p.name })),
                ],
            },
            {
                key: "category",
                icon: Tag,
                label: "Categoria",
                defaultV: "all",
                kind: "list",
                options: [
                    {
                        v: "all",
                        l: `Todas as categorias${categories.length ? ` (${categories.length})` : ""}`,
                    },
                    ...categories.map((c) => ({ v: c.id, l: c.name })),
                ],
            },
        ],
        [projects, categories]
    );

    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                ref.current &&
                !ref.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const activeChips = FILTER_DEFS.filter(
        (f) => filters[f.key] !== f.defaultV
    ).map((f) => ({
        key: f.key,
        icon: f.icon,
        label: f.label,
        value:
            f.key === "period"
                ? getPeriodLabel(filters)
                : f.options.find((o) => o.v === filters[f.key])?.l ||
                  String(filters[f.key]),
        def: f,
    }));

    const activeCount = activeChips.length;

    const resetAll = () => {
        FILTER_DEFS.forEach((f) => {
            (setFilter as (k: string, v: unknown) => void)(f.key, f.defaultV);
        });
        setFilter("from", null);
        setFilter("to", null);
    };

    const resetOne = (key: keyof Filters) => {
        const f = FILTER_DEFS.find((d) => d.key === key);
        if (f) {
            (setFilter as (k: string, v: unknown) => void)(key, f.defaultV);
        }
        if (key === "period") {
            setFilter("from", null);
            setFilter("to", null);
        }
    };

    // Conta queries de /reports em vôo — usado pra mostrar barra de progresso
    // no topo quando o user muda filtro (sem feedback, parecia que "nada
    // acontecia" porque o placeholderData mantém a tela com dados antigos).
    const fetching = useIsFetching({ queryKey: ["reports"] });

    return (
        <div className="relative" ref={ref}>
            {fetching > 0 && (
                <div className="absolute -top-1 left-0 right-0 h-0.5 overflow-hidden rounded-full bg-blue-100">
                    <div className="h-full w-1/3 bg-blue-500 animate-indeterminate" />
                </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
                <button
                    onClick={() => setOpen(!open)}
                    className={cn(
                        "inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-lg border transition-all",
                        activeCount > 0
                            ? "bg-blue-600 border-blue-600 text-white shadow-sm hover:bg-blue-700"
                            : "bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                    )}
                >
                    <FilterIcon className="w-4 h-4" />
                    <span>Filtros</span>
                    {activeCount > 0 && (
                        <span className="inline-flex items-center justify-center text-[10px] font-bold bg-white text-blue-600 rounded-full w-5 h-5">
                            {activeCount}
                        </span>
                    )}
                    <ChevronDown
                        className={cn(
                            "w-3.5 h-3.5 transition-transform",
                            open && "rotate-180"
                        )}
                    />
                </button>

                {activeChips.map((c) => (
                    <button
                        key={c.key}
                        onClick={() => resetOne(c.key)}
                        className="group inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-gray-200 hover:border-rose-200 hover:bg-rose-50 pl-2.5 pr-1.5 py-1.5 rounded-lg text-gray-700 transition-colors max-w-xs"
                    >
                        <c.icon className="w-3 h-3 text-gray-400 group-hover:text-rose-400 shrink-0" />
                        <span className="text-gray-500 shrink-0">
                            {c.label}:
                        </span>
                        <span className="text-gray-900 group-hover:text-rose-700 truncate">
                            {c.value}
                        </span>
                        <span className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center text-gray-400 group-hover:bg-rose-100 group-hover:text-rose-600 shrink-0">
                            <X className="w-3 h-3" />
                        </span>
                    </button>
                ))}

                {activeCount > 0 && (
                    <button
                        onClick={resetAll}
                        className="text-xs font-medium text-gray-500 hover:text-gray-900 px-2 py-1.5 rounded-lg"
                    >
                        Limpar tudo
                    </button>
                )}

                <div className="grow" />

                <button
                    onClick={() => setFilter("compare", !filters.compare)}
                    className={cn(
                        "inline-flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg border transition-colors",
                        filters.compare
                            ? "bg-blue-50 border-blue-200 text-blue-700"
                            : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                    )}
                >
                    <span
                        className={cn(
                            "relative inline-block w-7 h-4 rounded-full transition-colors",
                            filters.compare ? "bg-blue-600" : "bg-gray-300"
                        )}
                    >
                        <span
                            className={cn(
                                "absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all",
                                filters.compare ? "left-3.5" : "left-0.5"
                            )}
                        />
                    </span>
                    Comparar c/ período anterior
                </button>
            </div>

            {open && (
                <div className="absolute z-30 left-0 right-0 top-full mt-2 bg-white border border-gray-100 rounded-xl shadow-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <FilterIcon className="w-4 h-4 text-gray-400" />
                            <h3 className="text-sm font-semibold text-gray-800">
                                Filtrar dados
                            </h3>
                            <span className="text-xs text-gray-400">
                                · aplicado a todo o dashboard
                            </span>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            className="text-gray-400 hover:text-gray-700"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {FILTER_DEFS.map((f) => (
                            <FilterField
                                key={f.key}
                                def={f}
                                value={String(filters[f.key])}
                                filters={filters}
                                setFilter={setFilter}
                                isDefault={filters[f.key] === f.defaultV}
                                loading={f.kind === "list" && listsLoading}
                            />
                        ))}
                    </div>

                    <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                        <button
                            onClick={resetAll}
                            className="text-xs font-medium text-gray-500 hover:text-gray-900 px-3 py-1.5 rounded-lg flex items-center gap-1.5"
                        >
                            <X className="w-3.5 h-3.5" />
                            Limpar filtros
                        </button>
                        <button
                            onClick={() => setOpen(false)}
                            className="text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg shadow-sm"
                        >
                            Aplicar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

interface FilterFieldProps {
    def: FilterDef;
    value: string;
    filters: Filters;
    setFilter: <K extends keyof Filters>(k: K, v: Filters[K]) => void;
    isDefault: boolean;
    loading?: boolean;
}

function FilterField({
    def,
    value,
    filters,
    setFilter,
    isDefault,
    loading,
}: FilterFieldProps) {
    return (
        <div>
            <div className="flex items-center gap-1.5 mb-2">
                <def.icon className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    {def.label}
                </span>
                {!isDefault && (
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                )}
            </div>

            {def.kind === "period" ? (
                <PeriodField
                    value={value as PeriodPreset}
                    filters={filters}
                    setFilter={setFilter}
                    options={def.options}
                />
            ) : loading ? (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2 text-xs text-gray-400">
                        <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                        Carregando {def.label.toLowerCase()}…
                    </div>
                    <div className="p-2 space-y-1.5">
                        <DashSkeleton h={28} />
                        <DashSkeleton h={28} />
                        <DashSkeleton h={28} />
                    </div>
                </div>
            ) : (
                <SearchableList
                    options={def.options}
                    value={value}
                    onChange={(v) => {
                        (setFilter as (k: string, v: unknown) => void)(def.key, v);
                    }}
                    placeholder={`Buscar ${def.label.toLowerCase()}…`}
                />
            )}
        </div>
    );
}

interface PeriodFieldProps {
    value: PeriodPreset;
    filters: Filters;
    setFilter: <K extends keyof Filters>(k: K, v: Filters[K]) => void;
    options: { v: string; l: string }[];
}

function PeriodField({ value, filters, setFilter, options }: PeriodFieldProps) {
    const today = toISO(new Date());
    const isCustom = value === "custom";

    const presetRange = (period: PeriodPreset): [string, string] => {
        const iso = (d: Date) => d.toISOString().slice(0, 10);
        const daysAgo = (n: number) => iso(new Date(Date.now() - n * 86400000));
        switch (period) {
            case "today":
                return [today, today];
            case "yesterday":
                return [daysAgo(1), daysAgo(1)];
            case "7d":
                return [daysAgo(6), today];
            case "30d":
                return [daysAgo(29), today];
            case "90d":
                return [daysAgo(89), today];
            default:
                return [daysAgo(29), today];
        }
    };

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
                {options.map((o) => (
                    <button
                        key={o.v}
                        onClick={() => {
                            setFilter("period", o.v as PeriodPreset);
                            setFilter("from", null);
                            setFilter("to", null);
                        }}
                        className={cn(
                            "text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors",
                            value === o.v
                                ? "bg-blue-600 border-blue-600 text-white shadow-sm"
                                : "bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                        )}
                    >
                        {o.l}
                    </button>
                ))}
                <button
                    onClick={() => {
                        const [from, to] =
                            filters.from && filters.to
                                ? [filters.from, filters.to]
                                : presetRange(value);
                        setFilter("from", from);
                        setFilter("to", to);
                        setFilter("period", "custom");
                    }}
                    className={cn(
                        "inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors",
                        isCustom
                            ? "bg-blue-600 border-blue-600 text-white shadow-sm"
                            : "bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                    )}
                >
                    <Calendar className="w-3 h-3" />
                    Personalizado
                </button>
            </div>

            {isCustom && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50/50 border border-blue-100 rounded-xl p-3 space-y-3">
                    <div className="flex flex-wrap gap-1.5">
                        {[
                            { l: "Últ. 14 dias", days: 14 },
                            { l: "Últ. 60 dias", days: 60 },
                            { l: "Este mês", kind: "thisMonth" },
                            { l: "Mês passado", kind: "lastMonth" },
                        ].map((q) => (
                            <button
                                key={q.l}
                                onClick={() => {
                                    let from: string;
                                    let to: string;
                                    const now = new Date();
                                    if (q.kind === "thisMonth") {
                                        from = new Date(
                                            now.getFullYear(),
                                            now.getMonth(),
                                            1
                                        )
                                            .toISOString()
                                            .slice(0, 10);
                                        to = today;
                                    } else if (q.kind === "lastMonth") {
                                        from = new Date(
                                            now.getFullYear(),
                                            now.getMonth() - 1,
                                            1
                                        )
                                            .toISOString()
                                            .slice(0, 10);
                                        to = new Date(
                                            now.getFullYear(),
                                            now.getMonth(),
                                            0
                                        )
                                            .toISOString()
                                            .slice(0, 10);
                                    } else {
                                        from = new Date(
                                            Date.now() -
                                                ((q.days as number) - 1) * 86400000
                                        )
                                            .toISOString()
                                            .slice(0, 10);
                                        to = today;
                                    }
                                    setFilter("from", from);
                                    setFilter("to", to);
                                }}
                                className="text-[11px] font-medium px-2 py-1 rounded-md bg-white/70 border border-blue-100 text-blue-700 hover:bg-white hover:border-blue-300 transition-colors"
                            >
                                {q.l}
                            </button>
                        ))}
                    </div>

                    <RangeCalendar
                        from={filters.from}
                        to={filters.to}
                        max={today}
                        onChange={(from, to) => {
                            setFilter("from", from);
                            setFilter("to", to);
                        }}
                    />

                    {filters.from && filters.to && (
                        <div className="flex items-center justify-between bg-white/60 rounded-lg px-2.5 py-1.5 border border-blue-100">
                            <span className="text-[11px] text-gray-600 font-mono">
                                {fmtBR(filters.from)} → {fmtBR(filters.to)}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700">
                                <Calendar className="w-3 h-3" />
                                {daysBetween(filters.from, filters.to)}{" "}
                                {daysBetween(filters.from, filters.to) === 1
                                    ? "dia"
                                    : "dias"}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
